import React, { useState } from "react";
import {
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  AlertTriangle,
  Loader2,
  ArrowDownCircle,
  ArrowUpCircle,
  Database,
  Archive,
  Copy,
  Info,
} from "lucide-react";
import axios from "axios";
import { useLocalization } from "../contexts/LocalizationContext";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

interface FailureInfo {
  key: string;
  fr_label: string;
  en_label: string;
  severity: string;
  hint_fr: string;
  hint_en: string;
}

interface Transaction {
  paydrc_reference: string;
  switch_reference: string;
  telco_reference: string;
  status: string;
  status_description: string;
  method: string;
  action: string;
  amount: number;
  currency: string;
  customer_number: string;
  created_at: string | null;
  updated_at: string | null;
  failure: FailureInfo | null;
}

interface LookupResponse {
  found: boolean;
  source: "live" | "archive" | null;
  thirdparty_reference: string;
  transaction: Transaction | null;
}

const STATUS_STYLE: Record<string, { bg: string; ring: string; text: string; icon: React.ElementType }> = {
  Successful: { bg: "bg-emerald-500/15", ring: "ring-emerald-400/30", text: "text-emerald-300", icon: CheckCircle2 },
  Failed:     { bg: "bg-rose-500/15",    ring: "ring-rose-400/30",    text: "text-rose-300",    icon: XCircle },
  Pending:    { bg: "bg-amber-500/15",   ring: "ring-amber-400/30",   text: "text-amber-300",   icon: Loader2 },
  Submitted:  { bg: "bg-sky-500/15",     ring: "ring-sky-400/30",     text: "text-sky-300",     icon: Send },
};

const TransactionLookup: React.FC = () => {
  const { t, language } = useLocalization();
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LookupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const fr = language === "fr";

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const ref = reference.trim();
    if (ref.length < 3) {
      setError(fr ? "Veuillez saisir une reference d'au moins 3 caracteres." : "Please enter a reference of at least 3 characters.");
      return;
    }
    setError(null);
    setLoading(true);
    setData(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/dashboard/transactions/lookup`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { thirdparty_reference: ref },
      });
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, kind: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(kind);
    setTimeout(() => setCopied(null), 1500);
  };

  const tx = data?.transaction || null;
  const statusKey = tx?.status || "";
  const statusStyle = STATUS_STYLE[statusKey] || STATUS_STYLE["Submitted"];
  const StatusIcon = statusStyle.icon;

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 shadow-xl shadow-indigo-500/10 ring-1 ring-white/[0.06]">
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-indigo-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 left-1/4 w-56 h-56 rounded-full bg-cyan-400/12 blur-3xl pointer-events-none" />
        <div className="relative p-5 sm:p-6 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
            <Search className="w-5 h-5 text-indigo-300" />
          </div>
          <div className="min-w-0">
            <div className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/25 mb-1.5">
              {fr ? "Verification" : "Verification"}
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              {fr ? "Verifier le statut d'une transaction" : "Check a transaction status"}
            </h1>
            <p className="text-slate-300 text-xs sm:text-sm mt-1">
              {fr
                ? "Saisissez votre propre reference (thirdparty_reference) — celle envoyee dans la requete API. Recherche etendue automatiquement a l'archive si la transaction est plus ancienne que 7 jours."
                : "Type your own reference (thirdparty_reference) — the one you sent in the API request. Search automatically extends to the archive for transactions older than 7 days."}
            </p>
          </div>
        </div>
      </div>

      {/* Search form */}
      <form onSubmit={handleSearch} className="bg-white rounded-2xl border border-slate-200 p-5">
        <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
          {fr ? "Votre reference (thirdparty_reference)" : "Your reference (thirdparty_reference)"}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={fr ? "ex: ORDER-12345" : "e.g. ORDER-12345"}
            className="flex-1 px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || reference.trim().length < 3}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 text-sm font-semibold transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {fr ? "Rechercher" : "Search"}
          </button>
        </div>
        {error && (
          <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
      </form>

      {/* Result */}
      {data && !data.found && (
        <div className="bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-800 ring-1 ring-white/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-white font-semibold mb-1">
            {fr ? "Aucune transaction trouvee" : "No transaction found"}
          </h3>
          <p className="text-slate-400 text-sm">
            {fr ? `La reference "${data.thirdparty_reference}" n'a pas ete trouvee pour votre compte.` : `Reference "${data.thirdparty_reference}" was not found for your account.`}
          </p>
          <p className="text-slate-500 text-xs mt-3">
            {fr ? "Verifiez l'orthographe ou contactez le support si le probleme persiste." : "Check the spelling or contact support if the issue persists."}
          </p>
        </div>
      )}

      {data && data.found && tx && (
        <div className="space-y-4">
          {/* Status hero */}
          <div className={`relative bg-slate-900 rounded-2xl ring-1 ${statusStyle.ring} p-5 sm:p-6 overflow-hidden shadow-lg shadow-black/20`}>
            <div className={`absolute -top-12 -right-12 w-48 h-48 ${statusStyle.bg} rounded-full blur-3xl pointer-events-none`} />
            <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`h-12 w-12 rounded-xl ${statusStyle.bg} ring-1 ${statusStyle.ring} flex items-center justify-center`}>
                  <StatusIcon className={`h-5 w-5 ${statusStyle.text} ${statusKey === 'Pending' ? 'animate-spin' : ''}`} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                    {fr ? "Statut" : "Status"}
                  </div>
                  <div className={`text-2xl font-bold ${statusStyle.text}`}>{statusKey}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider ring-1 ${
                  data.source === 'live'
                    ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30'
                    : 'bg-slate-500/15 text-slate-300 ring-white/10'
                }`}>
                  {data.source === 'live' ? <Database className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                  {data.source === 'live' ? (fr ? 'Live' : 'Live') : (fr ? 'Archive' : 'Archive')}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider ring-1 ${
                  tx.action === 'debit'
                    ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30'
                    : 'bg-sky-500/15 text-sky-300 ring-sky-400/30'
                }`}>
                  {tx.action === 'debit' ? <ArrowDownCircle className="w-3 h-3" /> : <ArrowUpCircle className="w-3 h-3" />}
                  {tx.action === 'debit' ? (fr ? 'Depot' : 'Deposit') : (fr ? 'Retrait' : 'Payout')}
                </span>
              </div>
            </div>
          </div>

          {/* Failure hint */}
          {tx.failure && (
            <div className="bg-slate-900 rounded-2xl ring-1 ring-rose-400/25 p-4 flex gap-3">
              <Info className="w-4 h-4 text-rose-300 flex-shrink-0 mt-0.5" />
              <div className="text-sm leading-relaxed">
                <div className="font-semibold text-rose-200 mb-1">
                  {fr ? tx.failure.fr_label : tx.failure.en_label}
                </div>
                <div className="text-slate-300 text-xs">
                  {fr ? tx.failure.hint_fr : tx.failure.hint_en}
                </div>
              </div>
            </div>
          )}

          {/* Details */}
          <div className="bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] p-5 sm:p-6">
            <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-4">
              {fr ? "Details" : "Details"}
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <Field label={fr ? "Votre reference" : "Your reference"} value={data.thirdparty_reference} mono onCopy={(v) => handleCopy(v, "tp")} copied={copied === "tp"} />
              <Field label="PayDRC reference" value={tx.paydrc_reference} mono onCopy={(v) => handleCopy(v, "pd")} copied={copied === "pd"} />
              {tx.switch_reference && (
                <Field label="Switch reference" value={tx.switch_reference} mono onCopy={(v) => handleCopy(v, "sw")} copied={copied === "sw"} />
              )}
              {tx.telco_reference && (
                <Field label="Telco reference" value={tx.telco_reference} mono onCopy={(v) => handleCopy(v, "tl")} copied={copied === "tl"} />
              )}
              <Field label={fr ? "Montant" : "Amount"} value={`${tx.amount.toLocaleString()} ${tx.currency}`} highlight />
              <Field label={fr ? "Methode" : "Method"} value={<span className="capitalize">{tx.method}</span>} />
              <Field label={fr ? "Client" : "Customer"} value={tx.customer_number || "—"} mono />
              <Field label={fr ? "Cree le" : "Created at"} value={tx.created_at ? new Date(tx.created_at).toLocaleString() : "—"} />
              <Field label={fr ? "Mis a jour" : "Updated at"} value={tx.updated_at ? new Date(tx.updated_at).toLocaleString() : "—"} />
            </dl>

            {tx.status_description && (
              <div className="mt-5 pt-5 border-t border-white/[0.06]">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-2">
                  {fr ? "Message de l'operateur" : "Operator message"}
                </div>
                <div className="text-sm text-slate-300 bg-white/[0.03] rounded-lg p-3 ring-1 ring-white/[0.04] whitespace-pre-wrap break-words leading-relaxed">
                  {tx.status_description}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function Field({
  label,
  value,
  mono = false,
  highlight = false,
  onCopy,
  copied,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  highlight?: boolean;
  onCopy?: (v: string) => void;
  copied?: boolean;
}) {
  const stringValue = typeof value === "string" ? value : "";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">{label}</div>
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`flex-1 min-w-0 text-sm break-all ${mono ? "font-mono" : ""} ${
            highlight ? "text-white font-bold tabular-nums" : "text-slate-200"
          }`}
        >
          {value}
        </div>
        {onCopy && stringValue && (
          <button
            onClick={() => onCopy(stringValue)}
            className="p-1.5 text-slate-500 hover:text-white hover:bg-white/5 rounded transition-colors flex-shrink-0"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

export default TransactionLookup;
